import React, { useEffect, useState } from "react";
import "./SetAd.scss";
import {
  Input,
  Image,
  Row,
  Col,
  Divider,
  Select,
  DatePicker,
  InputNumber,
  Tooltip,
} from "antd";
import Helpers from "@utils/Helpers";
import { useUnit } from "contexts/UnitContext";
import dayjs from "dayjs";
import { InfoCircleOutlined } from "@ant-design/icons";

interface StepProps {
  adData: any;
  setAdData: (adData: any) => void;
  adType: any;
  selectedProduct: any;
}

const SetAd: React.FC<StepProps> = ({
  adData,
  setAdData,
  selectedProduct,
  adType,
}) => {
  const { units, convertCurrency } = useUnit();
  const originalPrice =
    convertCurrency(selectedProduct.price_per_tons, "USD", units.currency) *
    1000;
  useEffect(() => {
    const discounted =
      (1 - adData.discounted_percent / 100) *
      convertCurrency(selectedProduct.price_per_tons, "USD", units.currency) *
      1000;

    setAdData((prev: any) => ({
      ...prev,
      discounted_price: Helpers.toFixedNoRound(discounted, 2),
    }));
  }, []);

  const handleDiscountPercentageChange = (value: number) => {
    let discounted = 0;
    if (adData.discounted_unit === "tons") {
      discounted = originalPrice - (originalPrice * value) / 100;
    } else {
      const pricePerBale =
        convertCurrency(selectedProduct.current_price, "USD", units.currency) /
        selectedProduct.quantity;
      discounted = pricePerBale - (pricePerBale / 100) * value;
    }
    setAdData({
      ...adData,
      discounted_percent: value,
      discounted_price: Helpers.toFixedNoRound(discounted, 2),
    });
  };

  const handleDiscountedPriceChange = (value: number) => {
    let percent = 0;
    if (adData.discounted_unit === "tons") {
      percent = ((originalPrice - value) / originalPrice) * 100;
    } else {
      const pricePerBale =
        convertCurrency(selectedProduct.current_price, "USD", units.currency) /
        selectedProduct.quantity;
      percent = ((pricePerBale - value) / originalPrice) * 100;
    }

    setAdData((prev: any) => ({
      ...prev,
      discounted_price: value,
      discounted_percent: Helpers.toFixedNoRound(percent, 0),
    }));
  };

  const handleChangeUnit = (value: string) => {
    if (value === "tons") {
      const discounted =
        originalPrice - (originalPrice * adData.discounted_percent) / 100;
      setAdData({
        ...adData,
        discounted_price: Helpers.toFixedNoRound(discounted, 2),
        discounted_unit: value,
      });
    } else {
      const pricePerBale =
        convertCurrency(selectedProduct.current_price, "USD", units.currency) /
        selectedProduct.quantity;

      const discountedPrice =
        pricePerBale - (pricePerBale / 100) * adData.discounted_percent;

      setAdData({
        ...adData,
        discounted_price: Helpers.toFixedNoRound(discountedPrice, 2),
        discounted_unit: value,
      });
    }
  };

  return (
    <div className="SetAd">
      <div className="selected-product">
        <Row gutter={[16, 4]} className="custom-table">
          <Col span={6} className="selected-product-header">
            NAME
          </Col>
          <Col span={6} className="selected-product-header">
            MONTHLY SALES
          </Col>
          <Col span={6} className="selected-product-header">
            PRICE
          </Col>
          <Col span={6} className="selected-product-header">
            STOCK
          </Col>

          <Divider />

          <Col span={6} className="selected-product-info">
            <div className="item">
              <Image
                className="image"
                src={selectedProduct.images[0]}
                preview={false}
              />
              <div className="name">
                {selectedProduct.hay_type}{" "}
                <span className="id">ID: {selectedProduct.product_id}</span>
              </div>
            </div>
          </Col>
          <Col span={6} className="selected-product-info">
            800 Bales / mon
          </Col>
          <Col span={6} className="selected-product-info">
            $
            {Helpers.formatNumber(
              convertCurrency(
                selectedProduct.unit == "Ton"
                  ? selectedProduct.price_per_tons
                  : selectedProduct.price_per_bale,
                "USD",
                units.currency
              ),
              2
            )}{" "}
            / {units.weight}
          </Col>
          <Col span={6} className="selected-product-info">
            {selectedProduct.quantity} {selectedProduct.unit}
          </Col>
        </Row>
      </div>
      <div className="item-title">
        <b>Ad information</b>
      </div>
      <div className="ad-info">
        <div className="ad-info-item">
          <div className="ad-info-content">
            <span className="ad-info-label">
              Ads type{" "}
              <Tooltip
                placement="right"
                title={`Run for limited time promotions. Discount products or services with urgent call-to-action. Countdown timer displayed on the ad. Dedicated "Promoted Product" section on the homepage`}
              >
                <InfoCircleOutlined />
              </Tooltip>
            </span>
            <Select
              className="ad-info-select"
              value={adData?.product_ad_type_id}
              variant="outlined"
              size="large"
              options={adType.map((item: any) => ({
                label: item.name,
                value: item.product_ad_type_id,
              }))}
              onChange={(value: any) => {
                setAdData({ ...adData, product_ad_type_id: value });
              }}
            />
          </div>
        </div>
        <div className="ad-info-item">
          <div className="ad-info-content">
            <span className="ad-info-label">Duration</span>
            <Select
              className="ad-info-select"
              value={adData?.duration}
              variant="outlined"
              size="large"
              options={[
                { label: "1 week", value: 1 },
                { label: "2 weeks", value: 2 },
                { label: "3 weeks", value: 3 },
                { label: "4 weeks", value: 4 },
              ]}
              onChange={(value: any) => {
                setAdData({ ...adData, duration: value });
              }}
            />
          </div>
          <div className="ad-info-content">
            <span className="ad-info-label">Slot for ads</span>
            <Input
              disabled
              className="ad-info-input"
              variant="outlined"
              size="large"
              value={adData?.duration}
              suffix="slot"
            />
          </div>
        </div>
        <div className="ad-info-item">
          <div className="ad-info-content">
            <span className="ad-info-label">Start date</span>
            <DatePicker
              className="ad-info-input"
              variant="outlined"
              size="large"
              style={{ width: "100%" }}
              placeholder="YYYY-MM-DD"
              minDate={dayjs()}
              allowClear={false}
              value={dayjs(adData.start_time)}
              onChange={(value: any) => {
                setAdData({
                  ...adData,
                  start_time: dayjs(value).toISOString(),
                });
              }}
            />
          </div>
          <div className="ad-info-content">
            <span className="ad-info-label">End date</span>
            <DatePicker
              className="ad-info-input"
              variant="outlined"
              size="large"
              style={{ width: "100%" }}
              value={dayjs(adData.start_time).add(adData.duration, "week")}
              disabled
            />
          </div>
        </div>
        <div className="ad-info-item">
          <div className="ad-info-content">
            <span className="ad-info-label">Discounted rate</span>
            <InputNumber
              className="ad-info-input"
              variant="outlined"
              size="large"
              suffix="%"
              controls={false}
              value={adData.discounted_percent}
              min={5}
              max={100}
              onChange={(value: any) => handleDiscountPercentageChange(value)}
            />
          </div>
          <div className="ad-info-content">
            <span className="ad-info-label">Discounted price</span>
            <div className="price-unit">
              <InputNumber
                className="ad-info-input"
                variant="borderless"
                size="large"
                controls={false}
                value={adData.discounted_price}
                min={0}
                max={originalPrice}
                onChange={(value: any) => {
                  if (value > originalPrice) return;
                  handleDiscountedPriceChange(value);
                }}
              />
              <Select
                className="ad-info-select"
                value={adData.discounted_unit}
                variant="borderless"
                size="large"
                style={{ width: "115px" }}
                options={[
                  { label: `${units.currency} / ton`, value: "Ton" },
                  { label: `${units.currency} / bale`, value: "Bale" },
                ]}
                onChange={(value: any) => {
                  handleChangeUnit(value);
                }}
              />
            </div>
          </div>
        </div>
        <div className="ad-info-item">
          <div className="ad-info-content">
            <span className="ad-info-label">Discounted quantity</span>
            <InputNumber
              className="ad-info-input"
              variant="outlined"
              size="large"
              suffix={`${selectedProduct.unit.toLowerCase()}`}
              controls={false}
              value={adData?.discounted_quantity}
              max={selectedProduct.quantity}
              onChange={(value: any) => {
                setAdData({ ...adData, discounted_quantity: value });
              }}
            />
          </div>
          <div className="ad-info-content">
            <span className="ad-info-label">
              Total discounted value{" "}
              <Tooltip
                placement="right"
                title={`Total discounted value = Discounted price * Discounted price`}
              >
                <InfoCircleOutlined />
              </Tooltip>
            </span>
            <InputNumber
              disabled
              className="ad-info-input"
              variant="outlined"
              size="large"
              suffix={`${units.currency} / ${
                adData.discounted_unit === "tons" ? "tons" : "bales"
              }`}
              controls={false}
              value={Helpers.toFixedNoRound(
                adData?.discounted_quantity * adData?.discounted_price,
                2
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetAd;
